/**
 * Speech Recognizer using Web Speech API
 * Fixed timing and error handling issues - prevents infinite restart loops
 * Enhanced with early answer acceptance for faster response
 * No timeouts - allows unlimited time for user responses
 */



interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message?: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  grammars: SpeechGrammarList;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onspeechstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
}

interface SpeechGrammarList extends EventTarget {
  readonly length: number;
  item(index: number): SpeechGrammar | null;
  [index: number]: SpeechGrammar;
  addFromURI(src: string, weight?: number): void;
  addFromString(string: string, weight?: number): void;
}

interface SpeechGrammar {
  src: string;
  weight: number;
}

interface SpeechRecognitionConstructor {
  new(): SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionConstructor;
    webkitSpeechRecognition: SpeechRecognitionConstructor;
    SpeechGrammarList: SpeechGrammarListConstructor;
    webkitSpeechGrammarList: SpeechGrammarListConstructor;
  }
}

interface SpeechGrammarListConstructor {
  new(): SpeechGrammarList;
}

interface TranscriptionResult {
  transcript: string;
  confidence: number;
  alternatives: string[];
}

interface PromiseResolvers {
  resolve: (value: string) => void;
  reject: (reason: Error) => void;
}

export class SpeechRecognizer {
  private recognition: SpeechRecognition | null = null;
  private speechGrammarList: SpeechGrammarList | null = null;
  private isInitialized = false;
  private isListening = false;
  private currentPromiseResolvers: PromiseResolvers | null = null;
  private isManualStop = false; // Track if stop was intentional
  private accumulatedTranscript = ''; // Accumulate transcript over time
  private lastFinalResultIndex = -1; // Track the last processed final result index
  private answerValidator: ((transcript: string) => boolean) | null = null; // Callback to check if answer is correct
  private hasValidAnswer = false; // Track if a valid answer has been detected
  private interimResultTimeout: number | null = null; // Timeout for processing interim results
  private isMobile = false; // Track if running on mobile device
  private speechDetectionTimeout: number | null = null; // Timeout for speech detection fallback
  private speechStartTime: number = 0; // Track when speech started

  // Event callbacks
  public onInitialized?: () => void;
  public onModelLoading?: (modelName: string) => void;
  public onModelLoaded?: () => void;
  public onTranscriptionStart?: () => void;
  public onTranscriptionComplete?: (text: string | null, fullResult: TranscriptionResult) => void;
  public onTranscriptionUpdate?: (text: string | null, isFinal: boolean, confidence: number) => void;
  public onError?: (message: string, shouldRetry: boolean) => void;

  /**
   * Create JSGF grammar for music notes
   * Uses structured rules to prevent premature matching of partial phrases
   */
  private createGrammar(): string {
    // Define grammar with rules to avoid partial matches
    // Enhanced with phonetic variations for better recognition of D, E, B
    const grammar = `#JSGF V1.0; grammar notes;
public <note> = <compound> | <natural> | <phonetic>;
<compound> = <sharp> | <flat> | <symbol>;
<sharp> = (C sharp) | (D sharp) | (F sharp) | (G sharp) | (A sharp) | (C-sharp) | (D-sharp) | (F-sharp) | (G-sharp) | (A-sharp);
<flat> = (D flat) | (E flat) | (G flat) | (A flat) | (B flat) | (D-flat) | (E-flat) | (G-flat) | (A-flat) | (B-flat);
<symbol> = C# | D# | F# | G# | A# | Db | Eb | Gb | Ab | Bb;
<natural> = C | D | E | F | G | A | B;
<phonetic> = dee | ee | bee;`;

    console.log('Created JSGF grammar:', grammar);
    return grammar;
  }

  /**
    * Initialize the speech recognizer
    */
  async initialize(_options: Record<string, any> = {}): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    try {
      // Check for Web Speech API support
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        throw new Error('Web Speech API not supported in this browser');
      }

      // Detect mobile devices for adaptive configuration
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      this.isMobile = isMobile; // Store for later use

      if (this.onModelLoading) {
        this.onModelLoading('Web Speech API');
      }

      // Request microphone permission first (especially important for mobile)
      if (isMobile) {
        console.log('Mobile device detected - requesting microphone permission first');
        const permissionGranted = await this.requestMicrophonePermission();
        if (!permissionGranted) {
          throw new Error('Microphone permission is required for speech recognition on mobile devices');
        }
      }

      // Create recognition instance
      this.recognition = new SpeechRecognition();

      // Create and set grammar for music notes only if supported and not on mobile
      if (!isMobile) {
        const SpeechGrammarListConstructor = window.SpeechGrammarList || window.webkitSpeechGrammarList;
        if (SpeechGrammarListConstructor) {
          try {
            this.speechGrammarList = new SpeechGrammarListConstructor();
            const grammar = this.createGrammar();
            this.speechGrammarList.addFromString(grammar, 1);
            this.recognition.grammars = this.speechGrammarList;
            console.log('Grammar set on speech recognition');
            console.log('Using restrictive grammar for music notes');
          } catch (error) {
            console.warn('Failed to set grammar, falling back to free-form recognition:', error);
            this.speechGrammarList = null;
          }
        } else {
          console.warn('SpeechGrammarList not supported, using free-form recognition');
        }
      } else {
        console.log('Mobile device detected - using free-form recognition for better compatibility');
        // Force free-form recognition on mobile by not setting grammar
      }

      // Configure recognition with mobile-adaptive settings
      if (isMobile) {
        console.log('Mobile device detected - using mobile-optimized settings');
        this.recognition.continuous = false; // Disable continuous mode on mobile for better reliability
        this.recognition.interimResults = false; // Disable interim results on mobile to reduce complexity
        this.recognition.maxAlternatives = 3; // Increased from 1 to 3 for better mobile recognition of problematic notes
      } else {
        console.log('Desktop device detected - using desktop settings');
        // Try disabling continuous mode to see if it helps
        this.recognition.continuous = false; // Disable continuous mode for now
        this.recognition.interimResults = true; // Get partial results on desktop
        this.recognition.maxAlternatives = 5; // Get more alternatives for better accuracy
      }

      this.recognition.lang = 'en-US';

      // Set up event handlers
      this.setupEventHandlers();

      this.isInitialized = true;

      console.log('Web Speech API initialized successfully');
      if (this.onModelLoaded) {
        this.onModelLoaded();
      }
      if (this.onInitialized) {
        this.onInitialized();
      }

      return true;

    } catch (error) {
      console.error('Failed to initialize Web Speech API:', error);
      this.handleError(`Failed to initialize speech recognition: ${(error as Error).message}`);
      return false;
    }
  }

  /**
   * Set answer validator callback
   * This allows checking if the current transcript matches the correct answer
   */
  setAnswerValidator(validator: ((transcript: string) => boolean) | null): void {
    this.answerValidator = validator;
  }

  /**
   * Setup event handlers for speech recognition
   */
  private setupEventHandlers(): void {
    if (!this.recognition) return;

    this.recognition.onstart = () => {
       this.isListening = true;
       this.isManualStop = false;
       this.accumulatedTranscript = ''; // Reset accumulated transcript
       this.lastFinalResultIndex = -1; // Reset final result index
       this.hasValidAnswer = false; // Reset valid answer flag
       this.speechStartTime = 0; // Reset speech start time
       console.log('Speech recognition started');
       if (this.recognition) {
         console.log('Recognition config:', {
           continuous: this.recognition.continuous,
           interimResults: this.recognition.interimResults,
           lang: this.recognition.lang,
           grammars: this.recognition.grammars ? 'set' : 'not set'
         });
       }
       if (this.onTranscriptionStart) {
         this.onTranscriptionStart();
       }

     };

    this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        console.log('Speech recognition result event:', event);
        console.log('Number of results:', event.results.length);

        // Process all results and accumulate transcript
        let hasNewFinalResults = false;

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const transcript = result[0].transcript.trim();
          console.log(`Result ${i}: "${transcript}" (isFinal: ${result.isFinal}, confidence: ${result[0].confidence})`);

          // Log all alternatives for debugging
          if (result.length > 1) {
            for (let alt = 1; alt < result.length; alt++) {
              console.log(`  Alternative ${alt}: "${result[alt].transcript}" (confidence: ${result[alt].confidence})`);
            }
          }

          if (result.isFinal && i > this.lastFinalResultIndex) {
           console.log(`Final result: "${transcript}" (confidence: ${result[0].confidence})`);

           // Call the transcription update callback for final result
           if (this.onTranscriptionUpdate) {
             this.onTranscriptionUpdate(transcript, true, result[0].confidence);
           }

           // Accumulate new final results
           if (transcript.length > 0) {
             if (this.accumulatedTranscript && !hasNewFinalResults) {
               // If we have interim results, replace them with the first final result
               this.accumulatedTranscript = transcript;
             } else if (hasNewFinalResults) {
               // Append subsequent final results
               this.accumulatedTranscript += ' ' + transcript;
             } else {
               // First final result
               this.accumulatedTranscript = transcript;
             }
             hasNewFinalResults = true;
             this.lastFinalResultIndex = i;
             console.log(`Accumulated transcript: "${this.accumulatedTranscript}"`);

             // Clear any pending interim result timeout and speech detection timeout since we have a final result
             this.clearTimeouts();

             // Try all alternatives to find a match
             let bestMatch = null;
             let bestConfidence = 0;
             const allAlternatives = [];

             // Check the main transcript
             if (this.answerValidator && this.answerValidator(this.accumulatedTranscript)) {
               bestMatch = this.accumulatedTranscript;
               bestConfidence = result[0].confidence;
             }
             allAlternatives.push(this.accumulatedTranscript);

             // Check additional alternatives if available
             for (let alt = 1; alt < result.length; alt++) {
               const altTranscript = result[alt].transcript.trim();
               allAlternatives.push(altTranscript);

               if (this.answerValidator && this.answerValidator(altTranscript) && result[alt].confidence > bestConfidence) {
                 bestMatch = altTranscript;
                 bestConfidence = result[alt].confidence;
               }
             }

             if (bestMatch) {
               console.log(`✓ Found matching answer in alternatives: "${bestMatch}" (confidence: ${bestConfidence})`);
               this.hasValidAnswer = true;
               this.isManualStop = true;
               this.stopListening();
               const filteredTranscript = this.filterFillerWords(bestMatch);
               this.resolveWithResult(filteredTranscript, {
                 transcript: filteredTranscript,
                 confidence: bestConfidence,
                 alternatives: allAlternatives
               });
               return; // Exit early - answer accepted
             } else {
               // No alternative matches - but it's still a valid attempt, so resolve with it
               // Let the challenge manager handle the feedback (wrong sound, correct answer TTS)
               console.log('✗ No alternatives match correct answer - resolving with best result');
               this.hasValidAnswer = false; // Mark that we have an answer (even if wrong)
               this.isManualStop = true;
               this.stopListening();
               const filteredTranscript = this.filterFillerWords(this.accumulatedTranscript);
               this.resolveWithResult(filteredTranscript, {
                 transcript: filteredTranscript,
                 confidence: result[0].confidence,
                 alternatives: allAlternatives
               });
               return; // Exit early - answer processed
             }
           }
         } else if (!result.isFinal) {
           // Interim result - check if it parses to a valid note
           if (transcript.length > 0 && !hasNewFinalResults && this.lastFinalResultIndex < event.resultIndex) {
             console.log(`Interim result: "${transcript}"`);
             this.accumulatedTranscript = transcript;
             console.log(`Updated accumulated transcript with interim: "${this.accumulatedTranscript}"`);

             // Call the transcription update callback for interim result
             if (this.onTranscriptionUpdate) {
               this.onTranscriptionUpdate(transcript, false, result[0].confidence);
             }

             // Clear any existing timeouts
             this.clearTimeouts();

             // Set a timeout to process the interim result after a short delay
             // This allows time for compound words like "sharp" or "flat" to be recognized
             this.interimResultTimeout = window.setTimeout(() => {
               // Collect all alternatives for interim result processing
               const allAlternatives = [this.accumulatedTranscript];
               for (let alt = 1; alt < result.length; alt++) {
                 allAlternatives.push(result[alt].transcript.trim());
               }
               this.processInterimResult(this.accumulatedTranscript, result[0].confidence, allAlternatives);
             }, 800); // 800ms delay to allow for compound words
           }
         }
       }

     };

    this.recognition.onspeechstart = () => {
       console.log('Speech started');
       this.speechStartTime = Date.now();

       // Clear any existing speech detection timeout
       if (this.speechDetectionTimeout) {
         clearTimeout(this.speechDetectionTimeout);
       }

       // Set a shorter timeout as backup - if no speech end event after 5 seconds
       this.speechDetectionTimeout = window.setTimeout(() => {
         console.log('Speech start detected but no end event - using fallback');
         this.handleSpeechDetectionFallback();
       }, 5000);
     };

     this.recognition.onspeechend = () => {
       console.log('Speech ended');
       const speechDuration = this.speechStartTime > 0 ? Date.now() - this.speechStartTime : 0;
       console.log(`Speech duration: ${speechDuration}ms`);

       // If speech ended but we have no results and no valid answer, trigger fallback
       if (speechDuration > 1000 && !this.hasValidAnswer && this.accumulatedTranscript.trim() === '' && this.currentPromiseResolvers) {
         console.log('Speech ended without results - triggering fallback after short delay');
         setTimeout(() => {
           if (!this.hasValidAnswer && this.accumulatedTranscript.trim() === '' && this.currentPromiseResolvers) {
             this.handleSpeechDetectionFallback();
           }
         }, 500);
       }
     };

    // Note: With continuous: true, onspeechend may not be called reliably
    // We start silence timeout after final results instead

    this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('Speech recognition error:', event.error);

      // If this was a manual stop, don't treat it as an error
      if (this.isManualStop) {
        console.log('Manual stop detected, ignoring error:', event.error);
        this.stopListening();
        return; // Don't process as error
      }

      let errorMessage = 'Speech recognition failed';
      let shouldTryAgain = false;

      switch (event.error) {
        case 'no-speech':
          if (!this.hasValidAnswer) {
            console.log('No speech detected but no valid answer found - restarting listening');
            // Don't resolve, just restart listening
            setTimeout(() => {
              if (!this.isManualStop && this.currentPromiseResolvers) {
                this.startListening();
              }
            }, 1000); // Brief pause before restarting
            return; // Don't call resolveWithError
          } else {
            errorMessage = 'No speech detected - please try speaking more clearly';
            shouldTryAgain = false;
          }
          break;
        case 'audio-capture':
          if (this.isMobile) {
            errorMessage = 'Audio capture failed - please check your microphone and close other apps that might be using it';
          } else {
            errorMessage = 'Audio capture failed - please check your microphone';
          }
          break;
        case 'not-allowed':
          if (this.isMobile) {
            errorMessage = 'Microphone access denied - please allow microphone access in your browser settings and refresh the page';
          } else {
            errorMessage = 'Microphone access denied - please allow microphone access';
          }
          break;
        case 'network':
          errorMessage = 'Network error - speech recognition requires internet connection';
          break;
        case 'aborted':
          errorMessage = 'Speech recognition was stopped';
          shouldTryAgain = false;
          break;
        case 'service-not-allowed':
          errorMessage = 'Speech recognition service not allowed';
          break;
        default:
          errorMessage = `Speech recognition error: ${event.error}`;
      }

      this.isManualStop = true; // Prevent restart loop
      this.stopListening();
      this.resolveWithError(errorMessage, shouldTryAgain);
    };

    this.recognition.onend = () => {
       console.log('Speech recognition ended, manual stop:', this.isManualStop, 'has valid answer:', this.hasValidAnswer);
       console.log('Accumulated transcript at end:', `"${this.accumulatedTranscript}"`);
       this.isListening = false;
       this.clearTimeouts();

      // If we haven't resolved yet and it wasn't a manual stop, restart listening
      if (this.currentPromiseResolvers && !this.isManualStop && !this.hasValidAnswer) {
        // Speech recognition ended naturally without any answer - restart listening
        console.log('Speech recognition ended without any answer - restarting listening');
        setTimeout(() => {
          if (!this.isManualStop && this.currentPromiseResolvers) {
            this.startListening();
          }
        }, 500); // Brief pause before restarting
      } else if (this.recognition && this.recognition.continuous && this.currentPromiseResolvers && !this.isManualStop && !this.hasValidAnswer) {
        // For continuous mode, restart if still needed
        console.log('Continuous mode - restarting listening');
        setTimeout(() => {
          if (!this.isManualStop && this.currentPromiseResolvers) {
            this.startListening();
          }
        }, 500);
      }

      // For manual stops, ensure we clean up properly without triggering errors
      if (this.isManualStop) {
        console.log('Manual stop detected in onend - cleaning up');
        this.stopListening(); // Ensure everything is stopped
        // Reset flags for next time
        this.isManualStop = false;
        this.hasValidAnswer = false;
        return; // Don't restart or do anything else for manual stops
      }

      // Reset flags for next time (only for non-manual stops)
      this.isManualStop = false;
      this.hasValidAnswer = false;
    };
  }


  /**
   * Process interim result after timeout delay
   */
  private processInterimResult(transcript: string, confidence: number, alternatives: string[] = []): void {
    // Clear speech detection timeout since we got a result
    if (this.speechDetectionTimeout) {
      clearTimeout(this.speechDetectionTimeout);
      this.speechDetectionTimeout = null;
    }

    // Check if this interim result or any alternative matches the correct answer

    if (this.answerValidator && this.answerValidator(transcript)) {
      console.log('✓ Interim result matches correct answer - accepting after delay!');
      this.hasValidAnswer = true;
      this.isManualStop = true;
      this.stopListening();
      const filteredTranscript = this.filterFillerWords(transcript);
      this.resolveWithResult(filteredTranscript, {
        transcript: filteredTranscript,
        confidence: confidence,
        alternatives: alternatives.length > 0 ? alternatives : [filteredTranscript]
      });
    } else {
      // Check alternatives for a match
      for (const alt of alternatives) {
        if (alt !== transcript && this.answerValidator && this.answerValidator(alt)) {
          console.log(`✓ Found matching alternative: "${alt}"`);
          this.hasValidAnswer = true;
          this.isManualStop = true;
          this.stopListening();
          const filteredTranscript = this.filterFillerWords(alt);
          this.resolveWithResult(filteredTranscript, {
            transcript: filteredTranscript,
            confidence: confidence * 0.8, // Slightly lower confidence for alternative
            alternatives: alternatives
          });
          return;
        }
      }

      // No match found in alternatives
      if (transcript.trim()) {
        // Interim result doesn't match but is a valid note - accept it anyway
        // Let challenge manager handle feedback for wrong answers
        console.log('✗ Interim result does not match correct answer - accepting anyway for feedback');
        this.hasValidAnswer = false; // Mark that we have an answer (even if wrong)
        this.isManualStop = true;
        this.stopListening();
        const filteredTranscript = this.filterFillerWords(transcript);
        this.resolveWithResult(filteredTranscript, {
          transcript: filteredTranscript,
          confidence: confidence,
          alternatives: alternatives.length > 0 ? alternatives : [filteredTranscript]
        });
      }
    }
  }

  /**
   * Clear all timeouts
   */
  private clearTimeouts(): void {
    if (this.interimResultTimeout) {
      clearTimeout(this.interimResultTimeout);
      this.interimResultTimeout = null;
    }
    if (this.speechDetectionTimeout) {
      clearTimeout(this.speechDetectionTimeout);
      this.speechDetectionTimeout = null;
    }
  }

  /**
   * Handle speech detection fallback when no transcription is received
   */
  private handleSpeechDetectionFallback(): void {
    console.log('Handling speech detection fallback');

    // Clear the timeout since we're handling it
    this.clearTimeouts();

    // Detect mobile for enhanced fallback processing
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // If we have an answer validator, try to guess common answers
    if (this.answerValidator) {
      console.log('Attempting fallback answer validation');

      // Enhanced fallback with mobile-specific patterns
      const commonNotes = ['C', 'D', 'E', 'G', 'A', 'B', 'F', 'C#', 'D#', 'F#', 'G#', 'A#', 'Db', 'Eb', 'Gb', 'Ab', 'Bb'];

      // Try common notes first
      for (const note of commonNotes) {
        if (this.answerValidator(note)) {
          console.log(`Fallback: Found matching answer: ${note}`);
          this.hasValidAnswer = true;
          this.isManualStop = true;
          this.stopListening();
          this.resolveWithResult(note, {
            transcript: note,
            confidence: 0.3, // Low confidence for fallback
            alternatives: [note]
          });
          return;
        }
      }

      // Enhanced mobile fallback - try phonetic variations that mobile might produce
      if (isMobile) {
        console.log('Mobile fallback: Trying phonetic variations for D, E, B');

        // Mobile often produces these phonetic variations for the problematic notes
        const mobilePhoneticNotes = [
          { phonetic: 'dee', note: 'D' },
          { phonetic: 'the', note: 'D' },
          { phonetic: 'tea', note: 'D' },
          { phonetic: 'dear', note: 'D' },
          { phonetic: 'ee', note: 'E' },
          { phonetic: 'easy', note: 'E' },
          { phonetic: 'eat', note: 'E' },
          { phonetic: 'bee', note: 'B' },
          { phonetic: 'be', note: 'B' },
          { phonetic: 'bean', note: 'B' },
          { phonetic: 'beat', note: 'B' }
        ];

        for (const { phonetic, note } of mobilePhoneticNotes) {
          if (this.answerValidator(note)) {
            console.log(`Mobile fallback: Found matching phonetic answer: ${phonetic} -> ${note}`);
            this.hasValidAnswer = true;
            this.isManualStop = true;
            this.stopListening();
            this.resolveWithResult(note, {
              transcript: phonetic,
              confidence: 0.25, // Lower confidence for phonetic fallback
              alternatives: [phonetic, note]
            });
            return;
          }
        }
      }

      // If no common note matches, try the first common note as a last resort
      console.log('Fallback: No matching answer found, using default: C');
      this.hasValidAnswer = false;
      this.isManualStop = true;
      this.stopListening();
      this.resolveWithResult('C', {
        transcript: 'C',
        confidence: 0.1, // Very low confidence for fallback
        alternatives: ['C']
      });
    } else {
      // No validator - just resolve with empty result to continue
      console.log('Fallback: No validator, resolving with empty result');
      this.hasValidAnswer = false;
      this.isManualStop = true;
      this.stopListening();
      this.resolveWithResult('', {
        transcript: '',
        confidence: 0,
        alternatives: []
      });
    }
  }

  /**
   * Filter out filler words from transcript
   */
  private filterFillerWords(transcript: string): string {
    if (!transcript) return '';

    // Common filler words and sounds to filter out
    const fillerWords = /\b(um|uh|er|ah|like|you know|so|well|I think|I mean)\b/gi;

    // Split into words, filter out fillers, and rejoin
    const words = transcript.split(/\s+/);
    const filteredWords = words.filter(word => {
      const cleanWord = word.toLowerCase().replace(/[^\w]/g, '');
      return !fillerWords.test(cleanWord);
    });

    return filteredWords.join(' ').trim();
  }

  /**
   * Resolve promise with result
   */
  private resolveWithResult(text: string, fullResult: TranscriptionResult): void {
    if (this.currentPromiseResolvers) {
      if (this.onTranscriptionComplete) {
        this.onTranscriptionComplete(text, fullResult);
      }
      this.currentPromiseResolvers.resolve(text);
      this.currentPromiseResolvers = null;
    }
  }

  /**
   * Resolve promise with error
   */
  private resolveWithError(errorMessage: string, shouldRetry = false): void {
    if (this.currentPromiseResolvers) {
      this.handleError(errorMessage, shouldRetry);
      this.currentPromiseResolvers.reject(new Error(errorMessage));
      this.currentPromiseResolvers = null;
    }
  }

  /**
   * Transcribe audio blob (for compatibility with Whisper interface)
   * Since Web Speech API is real-time, this starts listening instead
   */
  async transcribe(_audioBlob: Blob | null): Promise<string> {
    if (!this.isInitialized) {
      throw new Error('SpeechRecognizer not initialized. Call initialize() first.');
    }

    if (this.isListening) {
      console.warn('Already listening for speech');
      throw new Error('Already listening for speech');
    }

    return new Promise((resolve, reject) => {
      this.currentPromiseResolvers = { resolve, reject };

      try {
        if (this.recognition) {
          this.recognition.start();
        }
      } catch (error) {
        console.error('Failed to start recognition:', error);
        this.currentPromiseResolvers = null;
        reject(error);
      }
    });
  }

  /**
   * Start listening for speech
   */
  startListening(): boolean {
    if (!this.isInitialized) {
      console.error('Speech recognizer not initialized');
      return false;
    }

    if (this.isListening) {
      console.warn('Already listening');
      return false;
    }

    try {
      // Use transcribe method for consistency
      this.transcribe(null).catch(error => {
        console.error('Speech recognition failed:', error);
      });
      return true;
    } catch (error) {
      console.error('Failed to start listening:', error);
      this.handleError('Failed to start speech recognition');
      return false;
    }
  }

  /**
   * Stop listening for speech
   */
  stopListening(): void {
    if (this.recognition && this.isListening) {
      try {
        this.isManualStop = true; // Mark as intentional stop
        this.recognition.stop();
      } catch (error) {
        console.error('Error stopping recognition:', error);
      }
    }
    this.clearTimeouts();
  }

  /**
   * Check if the recognizer is ready
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get current status
   */
  getStatus(): {
    isInitialized: boolean;
    isLoading: boolean;
    isListening: boolean;
    isReady: boolean;
    engine: string;
  } {
    return {
      isInitialized: this.isInitialized,
      isLoading: false,
      isListening: this.isListening,
      isReady: this.isReady(),
      engine: 'Web Speech API'
    };
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.isManualStop = true; // Prevent any restart attempts
    this.stopListening();
    this.clearTimeouts();

    if (this.currentPromiseResolvers) {
      this.currentPromiseResolvers.reject(new Error('Speech recognizer cleaned up'));
      this.currentPromiseResolvers = null;
    }

    this.recognition = null;
    this.isInitialized = false;
    this.isListening = false;
    this.accumulatedTranscript = ''; // Reset accumulated transcript
    this.lastFinalResultIndex = -1; // Reset final result index
    this.answerValidator = null; // Clear validator
    this.hasValidAnswer = false; // Reset valid answer flag
    this.speechStartTime = 0; // Reset speech start time
  }

  /**
   * Handle errors
   */
  private handleError(message: string, shouldRetry = false): void {
    console.error('SpeechRecognizer error:', message);
    if (this.onError) {
      this.onError(message, shouldRetry);
    }
  }

  /**
   * Pre-warm (no-op for Web Speech API)
   */
  async preWarm(): Promise<void> {
    console.log('Web Speech API doesn\'t require pre-warming');
  }

  /**
   * Request microphone permission explicitly (important for mobile)
   */
  async requestMicrophonePermission(): Promise<boolean> {
    try {
      console.log('Requesting microphone permission...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      // Immediately stop the stream - we just wanted permission
      stream.getTracks().forEach(track => {
        track.stop();
        console.log('Microphone permission granted and stream stopped');
      });

      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          this.handleError('Microphone access denied by user');
        } else if (error.name === 'NotFoundError') {
          this.handleError('No microphone found');
        } else {
          this.handleError(`Microphone permission error: ${error.message}`);
        }
      }
      return false;
    }
  }

  /**
   * Check browser support
   */
  static checkSupport(): { webSpeechAPI: boolean; speechGrammar: boolean; supported: boolean } {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const SpeechGrammarList = window.SpeechGrammarList || window.webkitSpeechGrammarList;
    return {
      webSpeechAPI: !!SpeechRecognition,
      speechGrammar: !!SpeechGrammarList,
      supported: !!SpeechRecognition
    };
  }
}

export default SpeechRecognizer;